import React, { useState, useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/api/auth';
import { 
  Users, 
  Search,
  CheckCircle,
  Zap,
  ShieldCheck,
  ChevronLeft,
  Play,
  Star,
  Users2
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import HeroSection from '../../components/sections/HeroSection';
import StatsSection from '../../components/sections/StatsSection';
import SearchDropdownPortal from '../../components/search/SearchDropdownPortal';

const Home = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInputRef, setSearchInputRef] = useState(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const searchContainerRef = useRef(null);

  // جلب البيانات العامة للمنصة (الكورسات المميزة والإحصائيات)
  const { data: homeData, isLoading } = useQuery(
    'home-data',
    async () => {
      const [courses, stats] = await Promise.all([
        authService.getPublicCourses({ limit: 6, featured: true }),
        authService.getPublicStats()
      ]);
      return {
        courses: courses.courses || [],
        stats: stats
      };
    }
  );

  // منطق البحث الفوري عن الكورسات والمدرسين مع debounce
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // Debounce 300ms

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { data: searchResults, isLoading: isSearching } = useQuery(
    ['search', debouncedSearchTerm],
    ({ signal }) => {
      if (!debouncedSearchTerm.trim()) return Promise.resolve({ courses: [], teachers: [] });
      
      return Promise.all([
        authService.searchCourses({ search: debouncedSearchTerm, limit: 8 }, signal),
        authService.searchTeachers({ search: debouncedSearchTerm, limit: 6 }, signal)
      ]).then(([courses, teachers]) => ({
        courses: courses.courses || [],
        teachers: teachers.teachers || []
      }));
    },
    {
      enabled: debouncedSearchTerm.trim().length > 0,
      keepPreviousData: true,
      staleTime: 60000, // Cache results for 1 minute
      cacheTime: 300000, // Keep cache for 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on cancelled requests
        if (error?.code === 'ERR_CANCELED') return false;
        // Retry max 1 time for other errors
        return failureCount < 1;
      },
      onError: (error) => {
        // Silently handle cancelled requests (normal in React Query)
        if (error?.code === 'ERR_CANCELED') return;
      }
    }
  );

  const stats = homeData?.stats || {};

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/courses?search=${encodeURIComponent(searchTerm)}`);
      setIsDropdownOpen(false);
    }
  };

  const handleLinkClick = () => {
    setIsDropdownOpen(false);
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Check if click is outside the search container AND outside the dropdown
      const isOutsideSearchContainer = searchContainerRef.current && 
        !searchContainerRef.current.contains(event.target);
      
      const isOutsideDropdown = !event.target.closest('[data-search-dropdown]');
      
      if (isOutsideSearchContainer && isOutsideDropdown) {
        setIsDropdownOpen(false);
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };

    // Update dropdown state based on search term
    setIsDropdownOpen(searchTerm.trim().length > 0);

    // Add event listeners
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [searchTerm, searchInputRef, searchContainerRef]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-white">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse tracking-widest uppercase text-xs">جاري تجهيز المنصة...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/50 overflow-x-hidden">
      
      {/* Hero Section with Enhanced Floating Search */}
      <HeroSection
        title={
          <span className="leading-tight">
            مستقبلك يبدأ من <span className="text-white underline decoration-white/20 underline-offset-8">هنا</span> <br />
            مع نخبة مدرسي الثانوية
          </span>
        }
        subtitle="تعلم بذكاء، تفوق بامتياز. المنصة التعليمية الأقوى التي تضع بين يديك أفضل الكورسات التعليمية والمحتوى المتميز لتصل إلى حلمك."
      >
        {/* Search Bar Container */}
        <div ref={searchContainerRef} className="relative max-w-3xl mx-auto z-50 px-4">
          <form onSubmit={handleSearchSubmit} className="relative group">
            <div className="absolute right-5 top-1/2 -translate-y-1/2 text-primary-200 group-focus-within:text-white transition-colors">
              <Search size={22} />
            </div>
            <input
              ref={setSearchInputRef}
              type="text"
              placeholder="ابحث عن كورس، مادة، أو مدرس..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pr-14 pl-6 py-5 text-lg border border-white/20 rounded-[2rem] bg-white/10 backdrop-blur-xl placeholder-primary-100 text-white focus:outline-none focus:ring-4 focus:ring-white/20 focus:bg-white/20 transition-all shadow-2xl"
            />
            <button 
              type="submit"
              className="absolute left-3 top-1/2 -translate-y-1/2 bg-white text-primary-700 px-6 py-2.5 rounded-2xl font-black text-sm hover:bg-primary-50 transition-all shadow-lg active:scale-95"
            >
              بحث
            </button>
          </form>

          {/* Real-time Search Results Dropdown */}
          {isDropdownOpen && (
            <SearchDropdownPortal inputRef={searchInputRef}>
              {/* Header */}
              <div className="sticky top-0 bg-white z-10 px-4 py-3 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-black text-gray-700">
                    نتائج البحث عن
                  </p>
                  <span className="text-sm font-bold text-primary-600 truncate">"{debouncedSearchTerm}"</span>
                </div>
                {!isSearching && (
                  <span className="text-xs font-bold text-gray-400 flex-shrink-0">
                    {(
                      (searchResults?.teachers?.length || 0) +
                      (searchResults?.courses?.length || 0)
                    )} نتيجة
                  </span>
                )}
              </div>

              <div className="max-h-[60vh] sm:max-h-96 overflow-y-auto">
                {isSearching ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto"></div>
                    <p className="mt-3 text-gray-400 font-bold text-sm">
                      جاري البحث...
                    </p>
                  </div>
                ) : (searchResults?.teachers?.length > 0 || searchResults?.courses?.length > 0) ? (
                  <div className="p-3 sm:p-4 space-y-4">

                    {/* Teachers */}
                    {searchResults?.teachers?.length > 0 && (
                      <div>
                        <p className="px-2 py-1 text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                          المدرسين ({searchResults.teachers.length})
                        </p>

                        <div className="space-y-2">
                          {searchResults.teachers.map((teacher) => (
                            <Link
                              key={`teacher-${teacher.teacher_id}-${teacher.teacher_subject_id || Math.random()}`}
                              to={`/teachers/${teacher.teacher_id}`}
                              onClick={handleLinkClick}
                              className="
                                flex items-center gap-3 p-3
                                rounded-xl
                                hover:bg-primary-50
                                transition-all group
                              "
                            >
                              <div className="w-11 h-11 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition">
                                <Users size={18} />
                              </div>

                              <div className="flex-1 min-w-0">
                                <p className="font-black text-gray-900 truncate group-hover:text-primary-600">
                                  {teacher.teacher_name}
                                </p>
                                <p className="text-xs text-gray-400 font-bold">
                                  {teacher.subject_name || 'مدرس'}
                                </p>
                              </div>

                              <ChevronLeft size={16} className="text-gray-300 group-hover:text-primary-600" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Courses */}
                    {searchResults?.courses?.length > 0 && (
                      <div>
                        <p className="px-2 py-1 text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                          الكورسات ({searchResults.courses.length})
                        </p>

                        <div className="space-y-2">
                          {searchResults.courses.map((course) => (
                            <Link
                              key={`course-${course.id}`}
                              to={`/courses/${course.id}`}
                              onClick={handleLinkClick}
                              className="
                                flex items-center gap-3 p-3
                                rounded-xl
                                hover:bg-primary-50
                                transition-all group
                              "
                            >
                              <div className="w-11 h-11 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition">
                                <Play size={18} fill="currentColor" />
                              </div>

                              <div className="flex-1 min-w-0">
                                <p className="font-black text-gray-900 truncate group-hover:text-primary-600">
                                  {course.title}
                                </p>
                                <div className="flex items-center gap-2 text-xs text-gray-400 font-bold">
                                  <span>{course.teacher_name}</span>
                                  {course.rating > 0 && (
                                    <>
                                      <span>•</span>
                                      <span className="flex items-center gap-0.5">
                                        <Star size={12} className="fill-yellow-400 text-yellow-400" />
                                        {parseFloat(course.rating).toFixed(1)}
                                      </span>
                                    </>
                                  )}
                                  {course.enrolled_count > 0 && (
                                    <>
                                      <span>•</span>
                                      <span className="flex items-center gap-0.5">
                                        <Users2 size={12} />
                                        {course.enrolled_count}
                                      </span>
                                    </>
                                  )}
                                </div>
                              </div>

                              <ChevronLeft size={16} className="text-gray-300 group-hover:text-primary-600" />
                            </Link>
                          ))}
                        </div>
                      </div>
                    )}

                  </div>
                ) : (
                  <div className="p-8 text-center text-gray-400 font-bold text-sm">
                    لا توجد نتائج مطابقة
                  </div>
                )}
              </div>
            </SearchDropdownPortal>
          )}
        </div>
      </HeroSection>

      {/* Why Choose Us Section - Reimagined */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-50/50 rounded-full blur-3xl -z-0"></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-sm font-black text-primary-600 uppercase tracking-[0.3em]">مميزات المنصة</h2>
            <h3 className="text-4xl font-black text-gray-900 leading-tight">لماذا يختار الطلاب منصتنا؟</h3>
            <p className="text-gray-500 max-w-2xl mx-auto">نحن نوفر بيئة تعليمية متكاملة تهدف إلى تبسيط المعلومة وتوفير الوقت والجهد للوصول إلى أعلى الدرجات.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="group bg-gray-50 p-10 rounded-[2.5rem] border border-transparent hover:border-blue-100 hover:bg-white hover:shadow-2xl transition-all duration-500 text-center">
              <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-blue-50 group-hover:scale-110 group-hover:rotate-3 transition-transform">
                <CheckCircle size={36} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-4">جودة المحتوى</h3>
              <p className="text-gray-500 text-sm leading-relaxed font-medium">فيديوهات عالية الدقة، ملازم PDF، واختبارات دورية تضمن لك التميز.</p>
            </div>
            
            <div className="group bg-gray-50 p-10 rounded-[2.5rem] border border-transparent hover:border-green-100 hover:bg-white hover:shadow-2xl transition-all duration-500 text-center">
              <div className="w-20 h-20 bg-green-100 text-green-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-green-50 group-hover:scale-110 group-hover:rotate-3 transition-transform">
                <Zap size={36} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-4">تعلم بحرية</h3>
              <p className="text-gray-500 text-sm leading-relaxed font-medium">شاهد دروسك في أي وقت، من أي مكان، وعبر أي جهاز متاح لديك.</p>
            </div>
            
            <div className="group bg-gray-50 p-10 rounded-[2.5rem] border border-transparent hover:border-purple-100 hover:bg-white hover:shadow-2xl transition-all duration-500 text-center">
              <div className="w-20 h-20 bg-purple-100 text-purple-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-purple-50 group-hover:scale-110 group-hover:rotate-3 transition-transform">
                <ShieldCheck size={36} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-4">دعم فني متميز</h3>
              <p className="text-gray-500 text-sm leading-relaxed font-medium">فريق كامل متواجد للرد على استفساراتك التقنية والتعليمية على مدار الساعة.</p>
            </div>
            
            <div className="group bg-gray-50 p-10 rounded-[2.5rem] border border-transparent hover:border-yellow-100 hover:bg-white hover:shadow-2xl transition-all duration-500 text-center">
              <div className="w-20 h-20 bg-yellow-100 text-yellow-600 rounded-3xl flex items-center justify-center mx-auto mb-8 shadow-lg shadow-yellow-50 group-hover:scale-110 group-hover:rotate-3 transition-transform">
                <Users size={36} />
              </div>
              <h3 className="text-xl font-black text-gray-900 mb-4">مجتمع تفاعلي</h3>
              <p className="text-gray-500 text-sm leading-relaxed font-medium">تواصل مع زملائك ومدرسينك في ساحات النقاش المخصصة لكل درس.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section - Animated Numbers Grid */}
      <div className="relative py-12">
        <StatsSection stats={stats} />
      </div>

      {/* Modern Footer CTA */}
      <section className="max-w-7xl mx-auto px-6 mb-24">
        <div className="bg-gray-900 rounded-[3.5rem] p-12 lg:p-20 text-white relative overflow-hidden group">
            <div className="absolute -top-24 -right-24 w-96 h-96 bg-primary-600/20 rounded-full blur-[100px] group-hover:bg-primary-600/30 transition-all duration-1000"></div>
            <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-10">
                <div className="text-center lg:text-right space-y-4">
                    <h2 className="text-3xl lg:text-5xl font-black leading-tight">هل أنت مستعد لبدء <br /> رحلة النجاح معنا؟</h2>
                    <p className="text-gray-400 font-bold text-lg max-w-xl">انضم الآن إلى آلاف الطلاب الذين حققوا أحلامهم من خلال منصتنا التعليمية.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                    {!isAuthenticated ? (
                        <>
                            <Link to="/register" className="bg-white text-gray-900 px-10 py-5 rounded-2xl font-black text-center shadow-xl hover:bg-primary-50 transition-all active:scale-95">انضم إلينا الآن</Link>
                            <Link to="/login" className="bg-white/10 backdrop-blur-md border border-white/20 text-white px-10 py-5 rounded-2xl font-black text-center hover:bg-white/20 transition-all">تسجيل الدخول</Link>
                        </>
                    ) : (
                        <Link to="/dashboard" className="bg-primary-600 text-white px-12 py-5 rounded-2xl font-black text-center shadow-2xl shadow-primary-900 hover:bg-primary-700 transition-all flex items-center gap-2 justify-center">
                            الذهاب للوحة التحكم <ChevronLeft size={20} />
                        </Link>
                    )}
                </div>
            </div>
        </div>
      </section>

    </div>
  );
};

export default Home;